<template>
  <a-drawer
    :title="$t('encaissementValidation.detailTitle')"
    :visible="visible"
    :width="640"
    @close="$emit('close')"
    destroy-on-close
  >
    <a-spin :spinning="loading">
      <template v-if="detail">
        <!-- Schedule info -->
        <a-descriptions bordered :column="1" size="small">
          <a-descriptions-item :label="$t('encaissementValidation.colMontant')">
            <span class="amount-cell">{{ (detail.schedule.amount || 0).toFixed(3) }}</span>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('encaissementValidation.colDueDate')">
            {{ formatDate(detail.schedule.dueDate) }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('encaissementValidation.colStatut')">
            <a-tag :color="statusColor(detail.schedule.status)">
              {{ $t('encaissementValidation.status' + capitalize(detail.schedule.status)) }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('encaissementValidation.colCreatedBy')">
            {{ detail.schedule.createdBy }}
          </a-descriptions-item>
          <a-descriptions-item v-if="detail.schedule.receivedAt" :label="$t('encaissementValidation.receivedAt')">
            {{ formatDate(detail.schedule.receivedAt) }}
          </a-descriptions-item>
        </a-descriptions>

        <!-- Depense info -->
        <a-divider orientation="left"><a-icon type="file-text" /> {{ $t('encaissementValidation.depenseInfo') }}</a-divider>
        <a-descriptions bordered :column="1" size="small">
          <a-descriptions-item :label="$t('encaissementValidation.colLibelle')">
            {{ detail.depense.label || '—' }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('encaissementValidation.colPayeur')">
            <a-icon :type="beneficiaryIcon(detail.depense.beneficiaryType)" class="mr-1" />
            {{ detail._beneficiaryName || '—' }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('decaissement.directionInverse')">
            <a-tag color="orange">
              <a-icon type="swap" class="mr-1" /> {{ $t('decaissement.directionInverse') }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('recette.montant')">
            <span class="amount-cell">{{ depenseTotal.toFixed(3) }}</span>
          </a-descriptions-item>
        </a-descriptions>

        <template v-if="depenseForPanel">
          <a-divider orientation="left"
            ><a-icon type="form" /> {{ $t("encaissementValidation.editRepaymentSchedulesSection") }}</a-divider
          >
          <inverse-schedules-panel
            hide-title
            :depense="depenseForPanel"
            :highlight-schedule-id="scheduleId"
            @refresh="onInverseSchedulesRefresh"
          />
        </template>

        <!-- Payment details (if received) -->
        <template v-if="detail.payment">
          <a-divider orientation="left"><a-icon type="dollar" /> {{ $t('encaissementValidation.paymentInfo') }}</a-divider>
          <a-descriptions bordered :column="1" size="small" class="mb-2">
            <a-descriptions-item :label="$t('recette.montant')">
              <span class="amount-cell">{{ (detail.payment.totalReceived || 0).toFixed(3) }}</span>
            </a-descriptions-item>
            <a-descriptions-item v-if="detail.payment.receiptNumber" :label="$t('decaissement.receiptNumber')">
              {{ detail.payment.receiptNumber }}
            </a-descriptions-item>
            <a-descriptions-item :label="$t('encaissementValidation.receivedBy')">
              {{ detail.payment.receivedBy }}
            </a-descriptions-item>
            <a-descriptions-item :label="$t('encaissementValidation.receivedAt')">
              {{ formatDate(detail.payment.receivedAt) }}
            </a-descriptions-item>
          </a-descriptions>

          <div v-for="(sp, spIdx) in displayPaymentSplits" :key="spIdx" class="payment-split-detail mb-3">
            <div class="split-detail-title">
              {{ $t('encaissementValidation.paymentLine', { n: spIdx + 1 }) }}
              <template v-if="displayPaymentSplits.length === 1">
                — <a-icon :type="methodIcon(sp.method)" class="mr-1" />
                {{ methodLabel(sp.method) }}
              </template>
            </div>
            <a-descriptions bordered :column="1" size="small">
              <a-descriptions-item v-if="displayPaymentSplits.length > 1" :label="$t('encaissementValidation.paymentMethod')">
                <a-icon :type="methodIcon(sp.method)" class="mr-1" />
                {{ methodLabel(sp.method) }}
              </a-descriptions-item>
              <a-descriptions-item :label="$t('recette.montant')">
                <span class="amount-cell">{{ (sp.amount != null ? sp.amount : detail.payment.totalReceived || 0).toFixed(3) }}</span>
              </a-descriptions-item>
              <a-descriptions-item v-if="sp._caisse" :label="$t('decaissement.cashName')">
                {{ sp._caisse.name }}
              </a-descriptions-item>
              <a-descriptions-item v-if="sp._bankAccount" :label="$t('decaissement.caisseBank')">
                {{ sp._bankAccount.name }}
              </a-descriptions-item>
            </a-descriptions>

            <template v-if="sp.methodDetails && sp.methodDetails.cheques && sp.methodDetails.cheques.length">
              <a-divider orientation="left"><a-icon type="credit-card" /> {{ $t('decaissement.paymentMethod.cheque') }}</a-divider>
              <a-table :columns="chequeColumns" :data-source="sp.methodDetails.cheques" :pagination="false" size="small" :row-key="(r, i) => 'ch-' + spIdx + '-' + i" bordered>
                <template slot="montant" slot-scope="text"><span class="amount-cell">{{ (text || 0).toFixed(3) }}</span></template>
              </a-table>
            </template>
            <template v-if="sp.methodDetails && sp.methodDetails.traites && sp.methodDetails.traites.length">
              <a-divider orientation="left"><a-icon type="file-protect" /> {{ $t('decaissement.paymentMethod.traite') }}</a-divider>
              <a-table :columns="traiteColumns" :data-source="sp.methodDetails.traites" :pagination="false" size="small" :row-key="(r, i) => 'tr-' + spIdx + '-' + i" bordered>
                <template slot="montant" slot-scope="text"><span class="amount-cell">{{ (text || 0).toFixed(3) }}</span></template>
              </a-table>
            </template>
            <template v-if="sp.methodDetails && sp.methodDetails.virements && sp.methodDetails.virements.length">
              <a-divider orientation="left"><a-icon type="bank" /> {{ $t('decaissement.paymentMethod.virement') }}</a-divider>
              <a-table :columns="virementColumns" :data-source="sp.methodDetails.virements" :pagination="false" size="small" :row-key="(r, i) => 'vi-' + spIdx + '-' + i" bordered>
                <template slot="montant" slot-scope="text"><span class="amount-cell">{{ (text || 0).toFixed(3) }}</span></template>
              </a-table>
            </template>
          </div>
        </template>

      </template>
      <a-empty v-else-if="!loading" />
    </a-spin>
  </a-drawer>
</template>

<script>
import moment from "moment";
import { fetchEncaissementDetail } from "@/services/api/compV3/encaissement";
import InverseSchedulesPanel from "../suppliers/components/InverseSchedulesPanel.vue";

export default {
  name: "EncaissementDetailDrawer",
  components: { InverseSchedulesPanel },
  props: {
    visible: { type: Boolean, default: false },
    scheduleId: { type: String, default: null },
  },
  data() {
    return { detail: null, loading: false };
  },
  computed: {
    depenseForPanel() {
      const d = this.detail?.depense;
      if (!d || !d._id) return null;
      const total = Number(d.amountTTC ?? d.amount ?? d.money ?? 0);
      return { ...d, _totalAmount: total };
    },
    depenseTotal() {
      const d = this.detail?.depense;
      if (!d) return 0;
      return d.amountTTC || d.amount || d.money || 0;
    },
    displayPaymentSplits() {
      const p = this.detail?.payment;
      if (!p || !p.splitPayments?.length) return [];
      return p.splitPayments;
    },
    chequeColumns() {
      const fmtDue = (text) => this.formatDate(text);
      return [
        { title: this.$t("decaissement.chequeNum"), dataIndex: "num", key: "num" },
        { title: this.$t("fournisseur.dueDate"), dataIndex: "dateEchance", key: "date", customRender: fmtDue },
        { title: this.$t("decaissement.chequeBank"), dataIndex: "bank", key: "bank" },
        { title: this.$t("decaissement.chequeTitulaire"), dataIndex: "titulaire", key: "tit" },
        { title: this.$t("recette.montant"), dataIndex: "montant", key: "mt", scopedSlots: { customRender: "montant" } },
      ];
    },
    traiteColumns() {
      const fmtDue = (text) => this.formatDate(text);
      return [
        { title: this.$t("decaissement.chequeNum"), dataIndex: "num", key: "num" },
        { title: this.$t("fournisseur.dueDate"), dataIndex: "dateEchance", key: "date", customRender: fmtDue },
        { title: this.$t("decaissement.chequeBank"), dataIndex: "bank", key: "bank" },
        { title: this.$t("decaissement.traiteRib"), dataIndex: "rib", key: "rib" },
        { title: this.$t("recette.montant"), dataIndex: "montant", key: "mt", scopedSlots: { customRender: "montant" } },
      ];
    },
    virementColumns() {
      return [
        { title: this.$t("decaissement.virementNum"), dataIndex: "num", key: "num" },
        { title: this.$t("decaissement.chequeTitulaire"), dataIndex: "titulaire", key: "tit" },
        { title: this.$t("recette.montant"), dataIndex: "montant", key: "mt", scopedSlots: { customRender: "montant" } },
      ];
    },
  },
  watch: {
    scheduleId(id) { if (id) this.loadDetail(id); else this.detail = null; },
    visible(v) { if (v && this.scheduleId) this.loadDetail(this.scheduleId); },
  },
  methods: {
    formatDate(d) { return d ? moment(d).format("DD/MM/YYYY") : "—"; },
    statusColor(s) {
      if (s === "received") return "green";
      if (s === "pending") return "orange";
      return "default";
    },
    capitalize(s) {
      if (!s) return "";
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
    beneficiaryIcon(type) {
      const map = { provider: "shop", associate: "team", employee: "user" };
      return map[type] || "user";
    },
    methodIcon(m) {
      const map = {
        cash: "wallet",
        cheque: "credit-card",
        virement: "bank",
        traite: "file-protect",
        tpe: "tablet",
      };
      return map[m] || "dollar";
    },
    methodLabel(m) {
      const key = "decaissement.paymentMethod." + m;
      const t = this.$t(key);
      return t !== key ? t : m || "—";
    },
    async loadDetail(id) {
      this.loading = true;
      try {
        const res = await fetchEncaissementDetail(id);
        this.detail = res.data;
      } catch {
        this.$message.error(this.$t("warning.connectionProblem"));
      } finally {
        this.loading = false;
      }
    },
    onInverseSchedulesRefresh() {
      if (this.scheduleId) this.loadDetail(this.scheduleId);
    },
  },
};
</script>

<style scoped>
.amount-cell { font-weight: 600; color: #1890ff; }
.split-detail-title {
  font-weight: 600;
  margin-bottom: 8px;
  color: #262626;
}
.payment-split-detail {
  padding-bottom: 8px;
  border-bottom: 1px dashed #f0f0f0;
}
.payment-split-detail:last-child {
  border-bottom: none;
}
</style>
